import { sanitizeErrorMessage } from '../utils/errorSanitizer';

/**
 * APIClient Service
 * Handles communication with backend API
 */
class APIClient {
  /**
   * Create an APIClient instance
   * @param {Object} config - Configuration object
   * @param {string} config.baseUrl - Base URL for API
   * @param {number} config.timeout - Request timeout in milliseconds
   * @param {string} config.apiKey - API key for dev mode authentication
   * @param {string} config.mode - Environment mode (dev/prod)
   */
  constructor(config) {
    this.baseUrl = config.baseUrl;
    this.timeout = config.timeout || 30000; // Default 30 seconds
    this.apiKey = config.apiKey || '';
    this.mode = config.mode || 'dev';
  }

  /**
   * Build URL with querystring parameters for authentication
   * @param {string} endpoint - API endpoint path
   * @returns {string} Full URL with authentication parameters
   */
  buildUrl(endpoint) {
    const url = `${this.baseUrl}${endpoint}`;

    // Add querystring params in dev mode
    if (this.mode === 'dev' && this.apiKey) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}api-key=${encodeURIComponent(this.apiKey)}&mode=${this.mode}`;
    }

    return url;
  }

  /**
   * Make an HTTP request using XMLHttpRequest
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Response data
   */
  async makeRequest(endpoint, data, options = {}) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const { onProgress, method = 'POST', errorContext = 'Request' } = options;

      // Setup progress tracking
      if (onProgress && typeof onProgress === 'function') {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            onProgress(percentComplete);
          }
        });
      }

      // Setup timeout
      const timeoutId = setTimeout(() => {
        xhr.abort();
        reject(this.createError('TIMEOUT', { message: 'Request timeout' }));
      }, this.timeout);

      // Handle successful completion
      xhr.addEventListener('load', () => {
        clearTimeout(timeoutId);
        this.handleResponse(xhr, resolve, reject, errorContext);
      });

      // Handle network errors
      xhr.addEventListener('error', () => {
        clearTimeout(timeoutId);
        reject(this.createError('NETWORK_ERROR', { message: 'Network connection failed' }));
      });

      // Handle abort
      xhr.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        reject(this.createError('TIMEOUT', { message: 'Request timeout' }));
      });

      // Open and send request
      xhr.open(method, this.buildUrl(endpoint));
      xhr.setRequestHeader('Content-Type', 'application/json');

      // Enable credentials (cookies) for cross-origin requests in production
      if (this.mode === 'prod') {
        xhr.withCredentials = true;
      }

      xhr.send(JSON.stringify(data));
    });
  }

  /**
   * Handle XHR response
   * @param {XMLHttpRequest} xhr - XMLHttpRequest object
   * @param {Function} resolve - Promise resolve function
   * @param {Function} reject - Promise reject function
   * @param {string} errorContext - Context for error messages
   */
  handleResponse(xhr, resolve, reject, errorContext) {
    if (xhr.status >= 200 && xhr.status < 300) {
      try {
        const data = JSON.parse(xhr.responseText);
        if (data.success === false) {
          reject(this.createError(xhr.status, { message: data.error || `${errorContext} failed` }));
        } else {
          resolve(data);
        }
      } catch (error) {
        reject(this.createError(xhr.status, { message: 'Invalid response format' }));
      }
    } else {
      try {
        const errorData = JSON.parse(xhr.responseText);
        const errorMessage = errorData.error || errorData.message || `${errorContext} failed`;
        reject(this.createError(xhr.status, { message: errorMessage }));
      } catch (error) {
        reject(this.createError(xhr.status, { message: `${errorContext} failed` }));
      }
    }
  }

  /**
   * Upload emails to the backend API with progress tracking
   * @param {string[]} emails - Array of email addresses
   * @param {Object} metadata - Optional metadata about the upload
   * @param {Function} onProgress - Progress callback function
   * @returns {Promise<Object>} Upload response
   */
  async uploadEmails(emails, metadata = {}, onProgress = null) {
    const data = await this.makeRequest(
      '/api/process-email-upload',
      {
        emails,
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString()
        }
      },
      { onProgress, errorContext: 'Upload' }
    );

    return {
      success: data.success || true,
      processed: data.processed || 0,
      stored: data.stored || 0,
      duplicates: data.duplicates || 0,
      errors: data.errors || []
    };
  }

  /**
   * Parse email file on backend
   * Sends base64 encoded file to backend for parsing
   * @param {Object} fileData - Object with fileContent, fileType, fileName, metadata
   * @returns {Promise<Object>} Parsed email data structure
   */
  async parseEmailFile(fileData) {
    const data = await this.makeRequest(
      '/api/process-email-upload',
      fileData,
      { errorContext: 'File parsing' }
    );

    return {
      type: data.type,
      headers: data.headers || [],
      rows: data.rows || [],
      emails: data.emails || [],
      totalRows: data.totalRows || 0
    };
  }

  /**
   * Validate email rows to check if they belong to same user or different users
   * Also checks for duplicate emails in DynamoDB
   * @param {Array<Object>} rows - Array of row objects containing email data
   * @param {Array<string>} headers - Array of column headers
   * @returns {Promise<Object>} Validation response with duplicate check results
   */
  async validateEmailRows(rows, headers) {
    const data = await this.makeRequest(
      '/api/validate-email-rows',
      { rows, headers },
      { errorContext: 'Validation' }
    );

    return {
      success: data.success || true,
      validationResults: data.validationResults || [],
      totalRows: data.totalRows || 0,
      summary: data.summary || {
        totalEmails: 0,
        newEmails: 0,
        existingEmails: 0,
        sameUserMatches: 0,
        differentUserMatches: 0
      }
    };
  }

  /**
   * Store validated emails in DynamoDB
   * @param {Array<Object>} emails - Array of email objects with metadata
   * @returns {Promise<Object>} Storage response
   */
  async storeEmails(emails) {
    const data = await this.makeRequest(
      '/api/store-emails',
      { emails },
      { errorContext: 'Storage' }
    );

    return {
      success: data.success || true,
      stored: data.stored || 0,
      failed: data.failed || 0,
      errors: data.errors || [],
      stored_emails: data.stored_emails || []
    };
  }

  /**
   * Upload emails with retry logic
   * @param {string[]} emails - Array of email addresses
   * @param {Object} metadata - Optional metadata
   * @param {Function} onProgress - Progress callback function
   * @param {number} maxRetries - Maximum number of retry attempts
   * @returns {Promise<Object>} Upload response
   */
  async uploadWithRetry(emails, metadata = {}, onProgress = null, maxRetries = 3) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await this.uploadEmails(emails, metadata, onProgress);
      } catch (error) {
        if (attempt === maxRetries || !this.isRetryable(error)) {
          throw error;
        }

        // Exponential backoff: 2^attempt seconds
        await this.delay(Math.pow(2, attempt) * 1000);
      }
    }
  }

  /**
   * Check if an error is retryable
   * @param {Error} error - Error object
   * @returns {boolean} True if error is retryable
   */
  isRetryable(error) {
    // Retry on network errors and 5xx server errors
    return (
      error.code === 'ETIMEDOUT' ||
      error.code === 'ECONNRESET' ||
      error.code === 'TIMEOUT' ||
      error.code === 'NETWORK_ERROR' ||
      (error.status && error.status >= 500)
    );
  }

  /**
   * Create a standardized error object
   * @param {number|string} status - HTTP status code or error code
   * @param {Object} data - Error data
   * @returns {Error} Standardized error
   */
  createError(status, data = {}) {
    const error = new Error(data.message || 'An error occurred');
    error.status = typeof status === 'number' ? status : null;
    error.code = typeof status === 'string' ? status : data.code || 'UNKNOWN_ERROR';
    error.data = data;
    return error;
  }

  /**
   * Handle and transform errors
   * @param {Error} error - Original error
   * @returns {Error} Transformed error
   */
  handleError(error) {
    // If already a structured error, sanitize and return it
    if (error.code && error.status !== undefined) {
      error.message = sanitizeErrorMessage(error.message);
      return error;
    }

    // Handle fetch/network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      const networkError = new Error('Network connection failed. Please check your internet connection.');
      networkError.code = 'NETWORK_ERROR';
      networkError.status = null;
      return networkError;
    }

    // Handle timeout errors
    if (error.name === 'AbortError' || error.code === 'TIMEOUT') {
      const timeoutError = new Error('Request timed out. Please try again.');
      timeoutError.code = 'TIMEOUT';
      timeoutError.status = null;
      return timeoutError;
    }

    // Default error - sanitize the message
    const sanitizedMessage = sanitizeErrorMessage(error.message || 'An unexpected error occurred');
    const defaultError = new Error(sanitizedMessage);
    defaultError.code = error.code || 'UNKNOWN_ERROR';
    defaultError.status = error.status || null;
    return defaultError;
  }

  /**
   * Delay execution for a specified time
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default APIClient;
